import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import { paramsChangeKey } from '@/app/lib/quant/previewParams'
import { quantApi } from '@/app/lib/quant/quantApi'
import { previewErrorKind } from '@/app/lib/quant/quantErrors'
import type { QuantPreviewStatus } from '@/ui/QuantPreviewPanel'
import { type CandlePoint, type ParamValue, type PreviewFrame, type PreviewInputRange, type PreviewOverlay, type PreviewSession, quantError } from '@/app/lib/quant/types'

export type { QuantPreviewStatus }

export interface PreviewMeta {
  data_through: number
  input_range: PreviewInputRange
  tf: string
  instrument: string
}

export interface PreviewErrorInfo {
  message: string
  code: string | null
  status: number
  missingDays?: string[]
  missingCount?: number
}

export interface QuantPreviewState {
  status: QuantPreviewStatus
  candles: CandlePoint[]
  total: number
  /** Latest applied overlay, or `null` before the first one. */
  overlay: PreviewOverlay | null
  /** The session's own request sequence of the overlay on screen. */
  overlaySeq: number
  /** Candle frames applied this session (a params edit must not change it). */
  candleFrames: number
  meta: PreviewMeta | null
  error: PreviewErrorInfo | null
  /** Bumped each time a request starts (status `loading` or `computing`); lets the view time one request. */
  pendingSeq: number
}

const INITIAL: QuantPreviewState = {
  status: 'loading',
  candles: [],
  total: 0,
  overlay: null,
  overlaySeq: 0,
  candleFrames: 0,
  meta: null,
  error: null,
  pendingSeq: 0,
}

interface KeyedState extends QuantPreviewState {
  key: string
}

type Action = { key: string; frame: PreviewFrame }

function statusForError(frame: { message: string; code: string | null; status: number }): QuantPreviewStatus {
  const kind = previewErrorKind(quantError(frame.status, frame.message, frame.code))
  if (kind === 'invalid_params') return 'invalid'
  if (kind === 'failed') return 'failed'
  if (kind === 'not_covered') return 'not_covered'
  // down, timeout, unconfigured, superseded, other: the service could not answer.
  return 'unavailable'
}

/** Pure frame reducer. A frame for a different session key starts from the initial state. */
export function previewReducer(prev: KeyedState, action: Action): KeyedState {
  const state: KeyedState = prev.key === action.key ? prev : { ...INITIAL, key: action.key }
  const frame = action.frame
  switch (frame.type) {
    case 'status': {
      if (frame.state === 'computing')
        return { ...state, status: 'updating', error: null, pendingSeq: state.pendingSeq + 1 }
      if (frame.state === 'loading') {
        // Keep what is on screen while a reload is under way.
        const held = state.candles.length > 0
        return {
          ...state,
          status: held ? 'updating' : 'loading',
          error: null,
          pendingSeq: state.pendingSeq + 1,
        }
      }
      if (frame.state === 'live') return { ...state, status: 'ready', error: null }
      return state
    }
    case 'meta':
      return {
        ...state,
        meta: {
          data_through: frame.data_through,
          input_range: frame.input_range,
          tf: frame.tf,
          instrument: frame.instrument,
        },
      }
    case 'candles':
      return {
        ...state,
        candles: frame.candles,
        total: frame.total,
        candleFrames: state.candleFrames + 1,
      }
    case 'overlay': {
      const { type: _type, seq, ...overlay } = frame
      void _type
      return { ...state, overlay, overlaySeq: seq }
    }
    case 'error':
      return {
        ...state,
        status: statusForError(frame),
        error: {
          message: frame.message,
          code: frame.code,
          status: frame.status,
          ...(frame.missing_days !== undefined ? { missingDays: frame.missing_days } : {}),
          ...(frame.missing_count !== undefined ? { missingCount: frame.missing_count } : {}),
        },
      }
  }
}

export interface UseQuantPreviewArgs {
  slug: string
  instrument: string
  tf: string
  bars: number
  /** Coerced param values, or `null` while any field is invalid (nothing is sent). */
  params: Record<string, ParamValue> | null
  /** False = no REST session is opened (the live view is serving this instrument). Default true. */
  enabled?: boolean
  /** Called once per applied overlay, with the number of candles held. */
  onRecomputed?: (info: { slug: string; instrument: string; tf: string; count: number }) => void
}

export interface UseQuantPreview extends QuantPreviewState {
  /** Starts a fresh request with the current params (no-op when no session is open). */
  retry: () => void
}

function hasRetry(session: PreviewSession): session is PreviewSession & { retry(): void } {
  return 'retry' in session && typeof (session as { retry?: unknown }).retry === 'function'
}

/**
 * Drives one REST preview session (spec 039). It consumes frames only; the
 * session behind `quantApi.openPreview` is the seam and owns the 400 ms
 * debounce. A strategy, instrument, timeframe or bars change closes the session
 * and opens a new one; a params edit sends only params (and only when something
 * other than `start`, `end`, `instrument` changed); invalid params pause
 * (everything kept, nothing sent); unmount closes the session.
 */
export function useQuantPreview({
  slug,
  instrument,
  tf,
  bars,
  params,
  enabled = true,
  onRecomputed,
}: UseQuantPreviewArgs): UseQuantPreview {
  const key = `${slug}|${instrument}|${tf}|${bars}`
  const [state, dispatch] = useReducer(previewReducer, { ...INITIAL, key })
  const sessionRef = useRef<PreviewSession | null>(null)
  const paramsRef = useRef(params)
  const sentKeyRef = useRef<string | null>(null)
  const recomputedRef = useRef(onRecomputed)
  const changeKey = params === null ? null : paramsChangeKey(params)

  // Open the session once the form has been valid at least once for this key.
  const [armedKey, setArmedKey] = useState<string | null>(null)
  if (params !== null && enabled && armedKey !== key) setArmedKey(key)
  const armed = armedKey === key && enabled

  useEffect(() => {
    paramsRef.current = params
    recomputedRef.current = onRecomputed
  })

  useEffect(() => {
    const initial = paramsRef.current
    if (!armed || initial === null) return
    const session = quantApi.openPreview({ slug, instrument, tf, bars, params: initial })
    sessionRef.current = session
    sentKeyRef.current = paramsChangeKey(initial)
    let count = 0
    const off = session.onFrame((frame) => {
      if (frame.type === 'candles') count = frame.candles.length
      if (frame.type === 'overlay') recomputedRef.current?.({ slug, instrument, tf, count })
      dispatch({ key, frame })
    })
    return () => {
      off()
      session.close()
      if (sessionRef.current === session) sessionRef.current = null
    }
  }, [armed, key, slug, instrument, tf, bars])

  useEffect(() => {
    if (changeKey === null || changeKey === sentKeyRef.current) return
    const next = paramsRef.current
    const session = sessionRef.current
    if (next === null || session === null) return
    sentKeyRef.current = changeKey
    session.sendParams(next)
  }, [changeKey])

  const retry = useCallback(() => {
    const session = sessionRef.current
    if (session !== null && hasRetry(session)) session.retry()
  }, [])

  // A session for another key has not produced a frame yet: show the initial state.
  const view = state.key === key ? state : { ...INITIAL, key }
  const { key: _key, ...rest } = view
  void _key
  return { ...rest, status: params === null ? 'invalid' : rest.status, retry }
}
